import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import {
  OPTIONAL_FEATURES,
  getFeature,
  defaultsRecord,
} from './registry';

/**
 * Reads every opt-in feature value once and exposes a setter that writes
 * through the registry's adapters and invalidates downstream query keys.
 */
export function useOptionalFeatures() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['optional-features', user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      if (!user?.id) return defaultsRecord();
      const entries = await Promise.all(
        OPTIONAL_FEATURES.map(async (f) => {
          try {
            return [f.id, await f.read(user.id)] as const;
          } catch {
            return [f.id, f.defaultEnabled] as const;
          }
        }),
      );
      return Object.fromEntries(entries) as Record<string, boolean>;
    },
  });

  const setValue = async (id: string, on: boolean) => {
    if (!user?.id) return;
    const f = getFeature(id);
    if (!f) return;
    await f.write(user.id, on);
    // Optimistic local update
    queryClient.setQueryData(
      ['optional-features', user.id],
      (prev: Record<string, boolean> | undefined) => ({
        ...(prev ?? defaultsRecord()),
        [id]: on,
      }),
    );
    // Invalidate downstream consumers
    for (const key of f.invalidateKeys(user.id)) {
      queryClient.invalidateQueries({ queryKey: key as readonly unknown[] });
    }
    queryClient.invalidateQueries({ queryKey: ['optional-features', user.id] });
  };

  return {
    values: query.data ?? defaultsRecord(),
    loading: query.isLoading,
    setValue,
    features: OPTIONAL_FEATURES,
  };
}
